import {
  getABTestConfig,
  lingoChatABTestRouteConfig,
} from '@/config/abTestConfig'
import navigateToNextTrialOnboardingPageAction from '@actions/navigateToNextTrialOnboardingPageAction'
import navigateToNextVoOnboardingPageAction from '@actions/navigateToNextVoOnboardingPageAction'
import { saveOnboardingAnswers } from '@actions/saveOnboardingAnswers'
import ABTestRootOutlet from '@app-studio-web/ui/components/ABTestRootOutlet'
import ABTestSectionOutlet from '@app-studio-web/ui/components/ABTestSectionOutlet'
import createAccountRoutes from '@app-studio-web/ui/pages/account/accountRoutes'
import {
  createStripeSubscriptionAction,
  getPricesLoader,
  getUserLoader,
  paymentCompletionRoute,
  paymentRoute,
} from '@app-studio-web/ui/pages/checkout/checkoutRoutes'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import helpCenterRoutes from '@app-studio-web/ui/pages/helpCenter/helpCenterRoutes'
import signUpRoutes from '@app-studio-web/ui/pages/signUp/signUpRoutes'
import { type ReactElement } from 'react'
import { Navigate, Outlet } from 'react-router-dom'
import { isTrialFunnel } from '../helpers/trialFunnel'
import { isVoFunnel } from '../helpers/voFunnel'
import CheckoutOutlet from './baseline/checkout/CheckoutOutlet'
import PlansPage from './baseline/checkout/PlansPage'
import baselineCheckoutRoutes from './baseline/checkout/routes'
import baselineOnboardingRoutes, {
  trialOnboardingQuestionRoutes,
} from './baseline/onboarding/routes'
import VariantPaymentPage from './checkout/VariantPaymentPage'
import FlowLandingPage from './flow/FlowLandingPage'
import LandingPage from './LandingPage'
import QuizOnboardingOutlet from './quiz/onboarding/QuizOnboardingOutlet'
import quizOnboardingRoutes from './quiz/onboarding/routes'
import shortOnboardingRoutes from './short/onboarding/routes'
import SignUpOutlet from './signUp/SignUpOutlet'
import VariantCompanyPage from './signUp/VariantCompanyPage'
import VariantEmailPage from './signUp/VariantEmailPage'
import VariantPasswordPage from './signUp/VariantPasswordPage'
import VariantPreparingPage from './signUp/VariantPreparingPage'
import TrialPlansPage from './trial/checkout/TrialPlansPage'
import TrialOnboardingOutlet from './trial/onboarding/TrialOnboardingOutlet'
import v1CheckoutRoutes from './v1/checkout/routes'
import v1OnboardingRoutes from './v1/onboarding/routes'
import v2CheckoutRoutes from './v2/checkout/routes'
import v2OnboardingRoutes from './v2/onboarding/routes'
const abTestConfig = getABTestConfig()
const trialOnboardingRoutes = trialOnboardingQuestionRoutes.map((route) => ({
  ...route,
  action: navigateToNextTrialOnboardingPageAction,
}))
// The "vo" funnel runs the short onboarding page sequence (same pages as the
// /onboarding short funnel), but with the vo navigation action + sticky flag so
// it lands on the /checkout/vo-plans (1/3/12-month -vo) pricing page.
const voOnboardingRoutes = shortOnboardingRoutes.map((route) => ({
  ...route,
  children: route.children?.map((child) => ({
    ...child,
    action: navigateToNextVoOnboardingPageAction,
  })),
}))

function resolvePlansRedirect(): string {
  if (isVoFunnel()) return '/checkout/vo-plans'
  if (isTrialFunnel()) return '/checkout/trial-plans'
  return '/checkout/plans'
}

// The vo and trial funnels sell different plans than the main funnel, so the
// payment page has to resolve prices with the same key set the funnel's sales
// page used — otherwise it summarises the wrong amount.
function resolvePricingVariant(): { pricingVariant: string } | undefined {
  if (isVoFunnel()) return { pricingVariant: 'vo' }
  if (isTrialFunnel()) return { pricingVariant: 'trial' }
  return undefined
}

function LingoPreparingPage() {
  return (
    <VariantPreparingPage
      onPrepare={saveOnboardingAnswers}
      redirectTo={resolvePlansRedirect()}
    />
  )
}

const variantSignUpElements: Record<string, ReactElement> = {
  company: <VariantCompanyPage />,
  email: <VariantEmailPage />,
  password: <VariantPasswordPage />,
  preparing: <LingoPreparingPage />,
}

const routes = [
  {
    element: <ABTestRootOutlet abTestConfig={abTestConfig || undefined} />,
    errorElement: <ErrorPage />,
    path: '/',
    children: [
      {
        element: <LandingPage />,
        index: true,
      },
      {
        element: <FlowLandingPage />,
        path: 'flow',
      },
      {
        element: <Outlet />,
        path: 'account',
        children: createAccountRoutes(),
      },
      {
        element: <CheckoutOutlet />,
        path: 'checkout',
        loader: getUserLoader,
        children: [
          ...baselineCheckoutRoutes,
          ...v1CheckoutRoutes,
          ...v2CheckoutRoutes,
          // Retired experiment URLs stay recoverable after variant removal.
          {
            element: <Navigate replace to="/checkout/plans" />,
            path: 'v1/plans',
          },
          {
            element: <Navigate replace to="/checkout/plans" />,
            path: 'v2/plans',
          },
          {
            action: createStripeSubscriptionAction,
            element: <TrialPlansPage />,
            loader: () => getPricesLoader({ pricingVariant: 'trial' }),
            path: 'trial-plans',
          },
          {
            action: createStripeSubscriptionAction,
            element: (
              <PlansPage
                hidePerks
                subscriptionPlanOrder={['first', 'top', 'second']}
              />
            ),
            loader: () => getPricesLoader({ pricingVariant: 'vo' }),
            path: 'vo-plans',
          },
          {
            ...paymentRoute,
            element: <VariantPaymentPage />,
            loader: ({ request }: { request: Request }) =>
              Promise.all([
                getPricesLoader(resolvePricingVariant()),
                getUserLoader({ request }),
              ]),
          },
          paymentCompletionRoute,
        ],
      },
      {
        element: <Outlet />,
        path: 'help-center',
        children: helpCenterRoutes,
      },
      {
        element: (
          <ABTestSectionOutlet
            basePath="/onboarding"
            getABTestConfig={getABTestConfig}
            routeConfig={lingoChatABTestRouteConfig}
            validVariants={['baseline', 'v1', 'v2']}
            variantPathPrefixes={{ v1: 'v1', v2: 'v2' }}
          />
        ),
        path: 'onboarding',
        children: [
          ...baselineOnboardingRoutes,
          ...v1OnboardingRoutes,
          ...v2OnboardingRoutes,
        ],
      },
      {
        element: <SignUpOutlet />,
        path: 'sign-up',
        children: [
          ...signUpRoutes.map((route) => {
            const element = route.path && variantSignUpElements[route.path]

            return element ? { ...route, element } : route
          }),
          {
            element: <Navigate replace to="/sign-up/company" />,
            path: 'v1/company',
          },
          {
            element: <Navigate replace to="/sign-up/password" />,
            path: 'v1/password',
          },
          {
            element: <Navigate replace to="/sign-up/email" />,
            path: 'v1/email',
          },
          {
            element: <Navigate replace to="/sign-up/preparing" />,
            path: 'v1/preparing',
          },
        ],
      },
      {
        element: <TrialOnboardingOutlet />,
        path: 'trial',
        children: [
          {
            index: true,
            element: <Navigate replace to="question-language" />,
          },
          ...trialOnboardingRoutes,
        ],
      },
      {
        element: <Outlet />,
        path: 'vo',
        children: [
          {
            index: true,
            element: <Navigate replace to="question-language" />,
          },
          ...voOnboardingRoutes,
        ],
      },
      {
        element: <QuizOnboardingOutlet />,
        path: 'quiz',
        children: [
          {
            index: true,
            element: <Navigate replace to="question-language" />,
          },
          ...quizOnboardingRoutes,
        ],
      },
      {
        element: <Navigate to="/onboarding" replace />,
        path: '*',
      },
    ],
  },
]

export default routes
